import { DataType, SortDirection } from "ka-table";
import { Column } from "ka-table/models";
import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import CrmTable from "../App/Components/Table/CrmTable";
import { useImportErrors } from "../App/Queries/ImportQueries";
import { useDebouncedHandler } from "../App/State/Hooks/useDebouncedHandler";
import { useImportTabStore } from "../App/State/Stores/ImportTabStore";
import { IImportError } from "../App/Typings/Imports";
import { formatPhone } from "../App/Utilities/formatUtilities";
import {
  AttachmentsPopover,
  ImportSearchBar,
  MaxSearchLength,
  paginationOptions,
  renderTablePaginationDisplay,
} from "./Components/SharedTableElements";

const columns: Column[] = [
  {
    title: "Row Number",
    key: "rowNumber",
    dataType: DataType.Number,
    sortDirection: SortDirection.Ascend,
    width: 140,
  },
  { title: "Name", key: "name", dataType: DataType.String },
  { title: "Email", key: "email", dataType: DataType.String },
  { title: "Phone", key: "phone", dataType: DataType.String },
  { title: "Title", key: "title", dataType: DataType.String },
  { title: "Company", key: "company", dataType: DataType.String },
  { title: "Location", key: "location", dataType: DataType.String },
  { title: "Attachments", key: "attachments", dataType: DataType.Object, isSortable: false },
  { title: "Reason", key: "reason", dataType: DataType.String },
  { title: "Error", key: "errorMessage", dataType: DataType.String },
];

export function ErrorsTab({ statusId }: { statusId: string }) {
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchInputError, setSearchInputError] = useState<string>("");

  const {
    query: { search, sortBy, sortDirection, page, pageSize },
    setPage,
    setSearch,
    handleRequestSort,
    handleChangePage,
    handleChangePageSize,
    toQueryString,
  } = useImportTabStore(
    useShallow((state) => ({
      query: state.query,
      setPage: state.setPage,
      setSearch: state.setSearch,
      handleRequestSort: state.handleRequestSort,
      handleChangePage: state.handleChangePage,
      handleChangePageSize: state.handleChangePageSize,
      toQueryString: state.toQueryString,
    })),
  );

  const [queryString, setQueryString] = useState<string>("");
  const { data: errors, isLoading } = useImportErrors(statusId, queryString);

  const itemCount = errors?.items ? errors.items.length : 0;
  const totalCount = errors?.totalItems ?? 0;
  const totalCountDisplay = totalCount > pageSize ? totalCount : itemCount;

  const handleSearchFilter = useDebouncedHandler((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > MaxSearchLength) {
      setSearchInputError(`Search input must be less than or equal to ${MaxSearchLength} characters`);
    } else {
      setSearchInputError("");
    }
    setSearchInput(e.target.value);
  }, 300);

  useEffect(() => {
    if (searchInput.length <= MaxSearchLength && searchInput !== search) {
      setSearch(searchInput);
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, search]);

  useEffect(() => {
    setQueryString(toQueryString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortBy, sortDirection, search]);

  return (
    <>
      <ImportSearchBar
        label="Filter Errors"
        placeholder="Search by row number, name, email, phone, or error message"
        defaultValue={search}
        error={searchInputError.length > 0}
        errorMessage={searchInputError}
        dataTestId="import-conflicts-searchbar"
        onChange={handleSearchFilter}
      />
      <CrmTable
        name="import-errors"
        columns={columns}
        loading={isLoading}
        data={errors?.items ?? []}
        pagination={{
          ...paginationOptions,
          currentPage: page,
          rows: pageSize,
          handleChangePage,
          handleChangeRowsPerPage: handleChangePageSize,
          totalCount: totalCountDisplay,
          labelDisplayedRows: ({ from, to, count }) =>
            renderTablePaginationDisplay({
              from,
              to,
              count,
              isLoading,
              search,
              itemName: "errors",
            }),
        }}
        order={sortDirection}
        orderBy={sortBy}
        onSort={handleRequestSort}
        rowTestId={(row: IImportError) => `row-${row.rowNumber}`}
        customCell={{
          elementAttributes: (props) => {
            let title = undefined;
            switch (props.column.key) {
              case "errorMessage":
                title = props.rowData.errorMessage;
                break;
              case "email":
                title = props.rowData.email;
                break;
              case "title":
                title = props.rowData.title;
                break;
              case "company":
                title = props.rowData.company;
                break;
              case "location":
                title = props.rowData.location;
                break;
              case "attachments":
                title = props.rowData.attachments
                  ? (props.rowData.attachments as string[]).join(", ")
                  : undefined;
                break;
            }
            return { title };
          },
          content: (props) => {
            if (props.column.key === "reason") {
              return getErrorDisplay(props.rowData.reason);
            }

            if (props.column.key === "name") {
              return `${props.rowData.firstName} ${props.rowData.lastName}`;
            }

            if (props.column.key === "phone" && props.rowData.phone) {
              return (
                <span
                  style={{
                    minWidth: "19ch",
                    display: "block",
                  }}
                >
                  {formatPhone(props.rowData.phone)}
                </span>
              );
            }

            if (props.column.key === "attachments") {
              return <AttachmentsPopover attachments={props.rowData.attachments as string[]} />;
            }
          },
        }}
      />
    </>
  );
}

export const getErrorDisplay = (reason: string) => {
  switch (reason) {
    case "CsvDuplicate":
      return "CSV Duplicate";
    default:
      return reason.replace(/([A-Z])/g, " $1").trim();
  }
};
